import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import supabase from '../../lib/supabase'
import ButtonsManager from './ButtonsManager'

const { 
  FiSettings, FiSave, FiRefreshCw, FiCheck, FiAlertCircle,
  FiDatabase, FiCode, FiPalette, FiMail, FiGlobe, FiShield,
  FiMousePointer
} = FiIcons

const Settings = () => {
  const [activeTab, setActiveTab] = useState('general')
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })

  const tabs = [
    { id: 'general', label: 'Γενικά', icon: FiSettings },
    { id: 'buttons', label: 'Κουμπιά', icon: FiMousePointer },
    { id: 'appearance', label: 'Εμφάνιση', icon: FiPalette },
    { id: 'integrations', label: 'Ενσωματώσεις', icon: FiGlobe },
    { id: 'security', label: 'Ασφάλεια', icon: FiShield }
  ]

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('codevaro_settings')
        .select('*')

      if (error) throw error

      const settingsObj = {}
      data?.forEach(setting => {
        settingsObj[setting.setting_key] = setting.setting_value
      })
      
      setSettings(settingsObj)
    } catch (error) {
      console.error('Error fetching settings:', error)
      setMessage({ type: 'error', text: 'Σφάλμα κατά τη φόρτωση των ρυθμίσεων' })
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    try {
      setSaving(true)
      
      // Convert settings object to array format for upsert
      const settingsArray = Object.entries(settings).map(([key, value]) => ({
        setting_key: key,
        setting_value: value
      }))

      for (const setting of settingsArray) {
        const { error } = await supabase
          .from('codevaro_settings')
          .upsert(setting, { onConflict: 'setting_key' })

        if (error) throw error
      }

      setMessage({ type: 'success', text: 'Οι ρυθμίσεις αποθηκεύτηκαν επιτυχώς!' })
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000)
    } catch (error) {
      console.error('Error saving settings:', error)
      setMessage({ type: 'error', text: 'Σφάλμα κατά την αποθήκευση των ρυθμίσεων' })
    } finally {
      setSaving(false)
    }
  }

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Γενικές Ρυθμίσεις</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Όνομα Ιστοσελίδας
                  </label>
                  <input
                    type="text"
                    value={settings.site_name || ''}
                    onChange={(e) => updateSetting('site_name', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="CodeVaro"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Περιγραφή Ιστοσελίδας
                  </label>
                  <textarea
                    value={settings.site_description || ''}
                    onChange={(e) => updateSetting('site_description', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Περιγραφή της ιστοσελίδας για SEO"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Επικοινωνίας
                  </label>
                  <input
                    type="email"
                    value={settings.contact_email || ''}
                    onChange={(e) => updateSetting('contact_email', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="info@codevaro.gr"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Τηλέφωνο Επικοινωνίας
                  </label>
                  <input
                    type="tel"
                    value={settings.contact_phone || ''}
                    onChange={(e) => updateSetting('contact_phone', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+30 210 123 4567"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={saveSettings}
                disabled={saving}
                className="btn-primary inline-flex items-center"
              >
                {saving ? (
                  <SafeIcon icon={FiRefreshCw} className="animate-spin mr-2" />
                ) : (
                  <SafeIcon icon={FiSave} className="mr-2" />
                )}
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </motion.button>
            </div>
          </div>
        )

      case 'buttons':
        return <ButtonsManager />

      case 'appearance':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ρυθμίσεις Εμφάνισης</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Κύριο Χρώμα
                  </label>
                  <input
                    type="color"
                    value={settings.primary_color || '#3B82F6'}
                    onChange={(e) => updateSetting('primary_color', e.target.value)}
                    className="w-full h-12 border border-gray-300 rounded-lg"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Δευτερεύον Χρώμα
                  </label>
                  <input
                    type="color"
                    value={settings.secondary_color || '#8B5CF6'}
                    onChange={(e) => updateSetting('secondary_color', e.target.value)}
                    className="w-full h-12 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Γραμματοσειρά
                  </label>
                  <select
                    value={settings.font_family || 'Inter'}
                    onChange={(e) => updateSetting('font_family', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Inter">Inter</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Lato">Lato</option>
                  </select>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.dark_mode === 'true'}
                      onChange={(e) => updateSetting('dark_mode', e.target.checked.toString())}
                      className="mr-2"
                    />
                    Σκοτεινό Θέμα
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={saveSettings}
                disabled={saving}
                className="btn-primary inline-flex items-center"
              >
                {saving ? (
                  <SafeIcon icon={FiRefreshCw} className="animate-spin mr-2" />
                ) : (
                  <SafeIcon icon={FiSave} className="mr-2" />
                )}
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </motion.button>
            </div>
          </div>
        )

      case 'integrations':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ενσωματώσεις</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google Analytics ID
                  </label>
                  <input
                    type="text"
                    value={settings.google_analytics_id || ''}
                    onChange={(e) => updateSetting('google_analytics_id', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="GA-XXXXXXXXX-X"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facebook Pixel ID
                  </label>
                  <input
                    type="text"
                    value={settings.facebook_pixel_id || ''}
                    onChange={(e) => updateSetting('facebook_pixel_id', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123456789012345"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={saveSettings}
                disabled={saving}
                className="btn-primary inline-flex items-center"
              >
                {saving ? (
                  <SafeIcon icon={FiRefreshCw} className="animate-spin mr-2" />
                ) : (
                  <SafeIcon icon={FiSave} className="mr-2" />
                )}
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </motion.button>
            </div>
          </div>
        )

      case 'security':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ρυθμίσεις Ασφάλειας</h3>
              <div className="space-y-6">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.enable_recaptcha === 'true'}
                      onChange={(e) => updateSetting('enable_recaptcha', e.target.checked.toString())}
                      className="mr-2"
                    />
                    Ενεργοποίηση reCAPTCHA
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    reCAPTCHA Site Key
                  </label>
                  <input
                    type="text"
                    value={settings.recaptcha_site_key || ''}
                    onChange={(e) => updateSetting('recaptcha_site_key', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={saveSettings}
                disabled={saving}
                className="btn-primary inline-flex items-center"
              >
                {saving ? (
                  <SafeIcon icon={FiRefreshCw} className="animate-spin mr-2" />
                ) : (
                  <SafeIcon icon={FiSave} className="mr-2" />
                )}
                {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </motion.button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <SafeIcon icon={FiRefreshCw} className="animate-spin h-8 w-8 text-blue-500" />
        <span className="ml-3 text-gray-600">Φόρτωση ρυθμίσεων...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Ρυθμίσεις</h2>
        <p className="text-gray-600">Διαχειριστείτε τις ρυθμίσεις της ιστοσελίδας</p>
      </div>

      {/* Message */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}
        >
          <div className="flex items-center">
            <SafeIcon 
              icon={message.type === 'success' ? FiCheck : FiAlertCircle} 
              className="mr-2" 
            />
            {message.text}
          </div>
        </motion.div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const IconComponent = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <SafeIcon icon={IconComponent} className="mr-2" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  )
}

export default Settings